import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Meldung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { meldungsIdAnzeige } from "../design/Kennung";
import { useRueckgaengig } from "../design/Rueckgaengig";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { umschlag } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";
import { einreihen } from "../plattform/warteschlange";
import { positionLesen, positionMessen } from "./geraeteposition";
import { ansprache, symbolDer, useEigenesRevier, type Rahmen } from "./lage";

/**
 * Ablauf „Liegeort“ (oberflaeche.md §5, design/README §9.6; F-160 … F-163): [Erlegung] → [Bemerkung] → „GPS-Messung“
 * bzw. „Karte“. Markieren ab der Startansicht (eigene Erlegungen, die noch ohne Liegeort liegen) meldet mit
 * Rückgängig-Fenster; Ändern aus der Bergeliste (`?schuss=<Meldungs-Id>`) geht ohne Fenster in die Warteschlange.
 * Titel in der Warteschlange: „Liegeort <Ansprache>“ (§1.5, V-3).
 */
export function LiegeortAblauf({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const [suche] = useSearchParams();
  const navigate = useNavigate();
  const rg = useRueckgaengig();
  const { uid } = useSitzung();
  const revier = useEigenesRevier(id);
  const bergestand = useSammlung(revier ? `drueckjagden/${id}/reviere/${revier}/bergeliste` : null);
  const eigenstand = useSammlung(uid ? `personen/${uid}/eigene_meldungen` : null);
  const bergeliste = bergestand.daten;
  const eigene = eigenstand.daten;
  const aendern = suche.get("schuss");
  const meine = new Set(eigene.filter((m) => m["_drueckjagd"] === id).map((m) => String(m["Schuss"])));
  const auswahl: Zeile[] = aendern ? bergeliste.filter((b) => b["Schuss"] === aendern)
    : bergeliste.filter((b) => meine.has(String(b["Schuss"])) && b["Erlegungsstatus"] === "liegt");
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [bemerkung, setBemerkung] = useState("");
  const [karte, setKarte] = useState(false);
  const [eingabe, setEingabe] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const erlegung = auswahl.length === 1 ? auswahl[0]! : auswahl.find((b) => b["Schuss"] === gewaehlt);
  const gesperrt = useSperre("ablauf", erlegung ? "angaben" : "erlegung");
  const zurueck = () => (aendern ? navigate(`/drueckjagd/${id}/bergeliste`, { replace: true }) : navigate(`/drueckjagd/${id}`, { replace: true }));

  const setzen = (quelle: "GPS-Messung" | "Karte", position: { Breite: number; Länge: number }, genauigkeit: number | null) => {
    if (!erlegung) return;
    const schuss = String(erlegung["Schuss"]);
    const daten: Record<string, unknown> = {
      Schuss: schuss, Liegeort: position, Quelle: quelle, ...(genauigkeit !== null ? { Genauigkeit: genauigkeit } : {}),
      ...(bemerkung.trim() ? { Bemerkung: bemerkung.trim() } : {}),
    };
    const titel = `Liegeort ${ansprache(erlegung)}`;
    if (aendern) void einreihen("LiegeortAendern", titel, umschlag(daten));
    else rg.vormerken({
      befehl: "LiegeortMarkieren", begriff: "Liegeort", symbol: "liegeort", titel: () => titel, daten,
      angaben: [
        genauigkeit !== null ? ["standort", `± ${genauigkeit} m`] : [quelle === "Karte" ? "karte" : "standort", quelle],
        [symbolDer(String(erlegung["Wildart"] ?? ""), erlegung["Altersklasse"] as string | null | undefined), ansprache(erlegung)],
      ],
    });
    zurueck();
  };

  const gps = async () => {
    setFehler(null);
    const m = await positionMessen();
    if (!m) { setFehler("Keine Position verfügbar."); return; }
    setzen("GPS-Messung", m.position, m.genauigkeit);
  };

  const ueberKarte = () => {
    const p = positionLesen(eingabe);
    if (!p) { setFehler("Keine Position verfügbar."); return; }
    setKarte(false);
    setzen("Karte", p, null);
  };

  // Erst mit den Erlegungen steht fest, ob ein Schritt „Erlegung“ nötig ist; vorher zeigt die Ansicht keine Auswahl.
  if (!revier || (bergestand.laedt && !bergeliste.length) || (eigenstand.laedt && !eigene.length)) return <div className="ansicht" aria-busy="true" />;
  return rahmen({
    drueckjagdId: id, name: "Liegeort", ablauf: erlegung ? "ablauf--liegeort" : "ablauf--kurz",
    titel: "Liegeort", schritte: erlegung ? ansprache(erlegung) : "Erlegung", sperre: gesperrt,
    children: (
      <>
        <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={() => (gewaehlt && auswahl.length > 1 ? setGewaehlt(null) : zurueck())} />
        {!erlegung ? (
          <div className="r12">
            <div className="raster raster--2" data-gleich="">
              {auswahl.map((b) => {
                const gleiche = auswahl.filter((x) => ansprache(x) === ansprache(b)).length > 1;
                const begriff = gleiche ? `${ansprache(b)} ${meldungsIdAnzeige(b["Schuss"])}` : ansprache(b);
                return <Kachel key={b._id} symbol="liegeort" begriff={begriff} art="wahl" onPress={() => setGewaehlt(String(b["Schuss"]))} />;
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="karte-gross platte" aria-hidden="true"><span className="marke-rund"><Symbol name="standort" /></span></div>
            <div>
              <Textfeld label="Bemerkung" wert={bemerkung} onChange={setBemerkung} />
              {fehler ? <Meldung>{fehler}</Meldung> : null}
              <div className="raster raster--2" data-gleich="">
                <Kachel symbol="karte" begriff="Karte" art="wahl" onPress={() => { setFehler(null); setKarte(true); }} />
                <Kachel symbol="standort" begriff="GPS-Messung" art="wahl" onPress={() => void gps()} />
              </div>
            </div>
          </>
        )}
        <Dialog name="Karte" symbol="karte" offen={karte} onSchliessen={() => setKarte(false)}>
          <Textfeld label="Position" wert={eingabe} onChange={setEingabe} mono eingabemodus="text" />
          <div className="knopfreihe">
            <Knopf art="zweit" onPress={() => setKarte(false)}>Abbrechen</Knopf>
            <Knopf onPress={ueberKarte}>Übernehmen</Knopf>
          </div>
        </Dialog>
      </>
    ),
  });
}
